import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Sparkles, Trophy, RotateCcw, ScanText, BookOpen, Gift, Crown,
  Frame as FrameIcon, Award, Gem, Palette, Layers, Type, Ticket, Coins,
  ScrollText, Orbit, Flame, Zap, ChevronRight, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const REWARD_META = {
  title: { icon: Crown, label: "Title" },
  frame: { icon: FrameIcon, label: "Frame" },
  badge: { icon: Award, label: "Badge" },
  artifact: { icon: Gem, label: "Artifact" },
  theme: { icon: Palette, label: "Theme" },
  letter_card: { icon: Layers, label: "Letter Card" },
  wordbody_card: { icon: Type, label: "Wordbody" },
  trial_token: { icon: Ticket, label: "Trial Token" },
  credits: { icon: Coins, label: "Credits" },
  certificate_progress: { icon: ScrollText, label: "Certificate" },
  universe_node: { icon: Orbit, label: "Universe" },
  xp: { icon: Zap, label: "XP" },
};

// Build small reward-hint chips from a choice's reward object (pre-decision).
function rewardHints(reward) {
  if (!reward) return [];
  const out = [];
  if (reward.title) out.push({ type: "title", label: "Title" });
  if (reward.frame) out.push({ type: "frame", label: "Frame" });
  if (reward.badge) out.push({ type: "badge", label: "Badge" });
  if (reward.artifact) out.push({ type: "artifact", label: "Artifact" });
  if (reward.theme) out.push({ type: "theme", label: "Theme" });
  if (reward.letter_card) out.push({ type: "letter_card", label: `Letter ${reward.letter_card}` });
  if (reward.wordbody_card) out.push({ type: "wordbody_card", label: "Wordbody" });
  if (reward.trial_token) out.push({ type: "trial_token", label: `${reward.trial_token.type} token` });
  if (reward.credits) out.push({ type: "credits", label: `+${reward.credits} cr` });
  if (reward.certificate) out.push({ type: "certificate_progress", label: "Cert progress" });
  if (reward.universe_node) out.push({ type: "universe_node", label: "Universe node" });
  return out;
}

const GoldChip = ({ type, children, testid }) => {
  const Meta = REWARD_META[type] || REWARD_META.badge;
  const Icon = Meta.icon;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] gold-edge gold-text bg-[rgba(176,141,60,0.10)]">
      <Icon className="w-3 h-3" /> {children}
    </span>
  );
};

export default function Path() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [tab, setTab] = useState("journey");
  const [archetypes, setArchetypes] = useState([]);
  const [run, setRun] = useState(null);
  const [name, setName] = useState("");
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [chestBusy, setChestBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadRewards = async () => {
    try { const r = await api.get("/path/rewards"); setRewards(r.data); } catch (e) { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get("/path/archetypes"), api.get("/path/current")]);
      setArchetypes(a.data.archetypes || []);
      setRun(c.data?.id ? c.data : null);
      await loadRewards();
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const start = async (archetype) => {
    setBusy(true);
    try {
      const r = await api.post("/path/start", { character_name: name || "You", archetype });
      setRun(r.data); setLastResult(null); setTab("journey");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not begin the journey"); }
    finally { setBusy(false); }
  };

  const choose = async (idx) => {
    setBusy(true);
    try {
      const r = await api.post("/path/choice", { run_id: run.id, choice_index: idx });
      setLastResult(r.data);
      await Promise.all([refresh(), loadRewards()]);
      (r.data.reward?.unlocked || []).forEach((u) => toast.success(`Unlocked: ${u.name}`));
      if (r.data.reward?.xp?.leveled_up) toast.success(`Level up — ${r.data.reward.xp.rank}!`);
      if (r.data.completed) {
        setRun({ ...run, completed: true, ending: r.data.ending, current_event: null, scores: r.data.scores, chapter: r.data.chapter });
      } else {
        setRun({ ...run, chapter: r.data.chapter, chapter_name: (r.data.next_event || {}).chapter_name, scores: r.data.scores, current_event: r.data.next_event });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Choice failed"); }
    finally { setBusy(false); }
  };

  const equip = async (slot, value) => {
    try {
      await api.post("/path/rewards/equip", { slot, value });
      await loadRewards();
      toast.success(`Equipped ${slot}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not equip"); }
  };

  const openChest = async () => {
    setChestBusy(true);
    try {
      const r = await api.post("/path/chest/open", {});
      if (r.data.opened) {
        toast.success(`Weekly chest: ${r.data.reward?.name}`);
        await Promise.all([refresh(), loadRewards()]);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Chest unavailable"); }
    finally { setChestBusy(false); }
  };

  if (loading) return <div className="text-[var(--bone-faint)] text-sm" data-testid="path-loading">Loading Path Mode…</div>;

  const mult = rewards?.streak_multiplier || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold" data-testid="path-title">Path Mode</h1>
          <p className="text-[var(--bone-muted)] text-sm" style={{ maxWidth: "60ch" }}>
            A ten-chapter symbolic journey — Origin to Legacy. Every choice teaches the doctrine and earns rewards.
          </p>
        </div>
        {mult > 1 && (
          <span data-testid="path-streak-multiplier" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs gold-edge gold-text bg-[rgba(176,141,60,0.10)]">
            <Flame className="w-3.5 h-3.5" /> ×{mult} streak boost
          </span>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white/[0.04] border border-white/[0.08]">
          <TabsTrigger value="journey" data-testid="path-tab-journey">Journey</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="path-tab-rewards">Rewards</TabsTrigger>
        </TabsList>

        {/* ---------------- JOURNEY ---------------- */}
        <TabsContent value="journey" className="mt-5">
          <JourneyView
            run={run} archetypes={archetypes} name={name} setName={setName}
            start={start} choose={choose} busy={busy} lastResult={lastResult}
            navigate={navigate} mult={mult}
            playAgain={() => { setRun(null); setLastResult(null); }}
            goRewards={() => setTab("rewards")}
          />
        </TabsContent>

        {/* ---------------- REWARDS ---------------- */}
        <TabsContent value="rewards" className="mt-5">
          <RewardsView rewards={rewards} equip={equip} openChest={openChest} chestBusy={chestBusy} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================= JOURNEY ============================= */
function JourneyView({ run, archetypes, name, setName, start, choose, busy, lastResult, navigate, mult, playAgain, goRewards }) {
  // Setup screen
  if (!run) {
    return (
      <div className="space-y-5 fade-in">
        <div className="litra-card p-5">
          <label className="text-sm text-[var(--bone-muted)]">Your name in this story</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="You"
            className="bg-white/[0.04] border-white/[0.1] mt-1 max-w-xs" data-testid="path-character-name" />
        </div>
        <div>
          <div className="text-sm font-display font-semibold mb-3">Choose your archetype — each has a unique 10-chapter arc</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {archetypes.map((p) => (
              <button key={p.id} onClick={() => start(p.id)} disabled={busy}
                className="litra-card litra-card-hover p-4 text-left disabled:opacity-50" data-testid={`path-archetype-${p.id}`}>
                <div className="font-display font-semibold">{p.name}</div>
                <p className="mt-1 text-sm text-[var(--bone-muted)]">{p.blurb}</p>
                {p.tagline && <p className="mt-2 text-xs green-text italic">“{p.tagline}”</p>}
              </button>
            ))}
            {busy && <div className="col-span-full text-center py-2"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>}
          </div>
        </div>
      </div>
    );
  }

  // Ending screen
  if (run.completed && run.ending) {
    const log = run.ending.rewards_log || [];
    return (
      <div className="space-y-5 fade-in">
        <div className="litra-card p-7 text-center" data-testid="path-ending">
          <Trophy className="w-10 h-10 mx-auto gold-text" />
          <h2 className="mt-3 text-2xl font-display font-semibold">{run.ending.title}</h2>
          <p className="mt-2 text-[var(--bone-muted)]" style={{ maxWidth: "62ch", margin: "0.5rem auto" }}>{run.ending.text}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
            <span className="mono-chip green-text" data-testid="path-ending-rep">Reputation {run.scores?.reputation ?? 0}</span>
            <span className="mono-chip text-[var(--bone-faint)]">Value {run.scores?.value ?? 0}</span>
            <span className="mono-chip text-[var(--bone-faint)]">Audience {run.scores?.audience ?? 0}</span>
          </div>
          {log.length > 0 && (
            <div className="mt-5 text-left max-w-md mx-auto">
              <div className="text-xs font-display font-semibold text-[var(--bone-muted)] mb-2">Rewards earned on this journey</div>
              <div className="flex flex-wrap gap-1.5">
                {log.map((u, i) => <GoldChip key={i} type={u.type} testid={`path-ending-reward-${i}`}>{u.name}</GoldChip>)}
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-center gap-2">
            <Button onClick={playAgain} className="bg-[var(--primary)] hover:opacity-90" data-testid="path-play-again"><RotateCcw className="w-4 h-4 mr-2" />New journey</Button>
            <Button variant="outline" className="glass" onClick={goRewards} data-testid="path-view-rewards">View rewards</Button>
          </div>
        </div>
      </div>
    );
  }

  const ev = run.current_event;
  const total = run.total_chapters || 10;
  const pct = Math.round(((run.chapter - 1) / total) * 100);

  return (
    <div className="space-y-5 fade-in">
      {/* Header / status */}
      <div className="litra-card p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="text-sm font-display font-semibold">{run.character_name}</div>
            <Badge variant="outline" className="border-[rgba(185,192,204,0.3)]">{run.archetype_name}</Badge>
          </div>
          <div className="text-sm text-[var(--bone-muted)]" data-testid="path-chapter-label">
            Chapter <b className="text-[var(--bone)]">{Math.min(run.chapter, total)}</b> / {total} — {run.chapter_name}
          </div>
        </div>
        <Progress value={pct} className="h-1.5" data-testid="path-progress" />
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="mono-chip green-text" data-testid="path-meter-reputation">Reputation {run.scores?.reputation ?? 0}</span>
          <span className="mono-chip text-[var(--bone-faint)]" data-testid="path-meter-value">Value {run.scores?.value ?? 0}</span>
          <span className="mono-chip text-[var(--bone-faint)]" data-testid="path-meter-audience">Audience {run.scores?.audience ?? 0}</span>
          {mult > 1 && <span className="mono-chip gold-text">×{mult} XP</span>}
        </div>
      </div>

      {/* Last result */}
      {lastResult && !lastResult.completed && (
        <div className="litra-card p-4 fade-in" style={{ borderColor: "rgba(20,90,67,0.4)" }} data-testid="path-last-result">
          <div className="text-sm">{lastResult.result?.result}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge className="gold-edge gold-text bg-transparent"><Sparkles className="w-3 h-3 mr-1" />+{lastResult.reward?.xp?.granted} XP</Badge>
            {(lastResult.reward?.unlocked || []).map((u, i) => <GoldChip key={i} type={u.type} testid={`path-unlocked-${i}`}>{u.name}</GoldChip>)}
            <div className="ml-auto flex gap-2">
              {lastResult.lesson_unlock && (
                <Button size="sm" variant="outline" className="glass" onClick={() => navigate("/doctrine")} data-testid="path-lesson-cta"><BookOpen className="w-3 h-3 mr-1" />Lesson</Button>
              )}
              {lastResult.decode_prompt && (
                <Button size="sm" variant="outline" className="glass" onClick={() => navigate("/decode")} data-testid="path-decode-cta"><ScanText className="w-3 h-3 mr-1" />Decode: {lastResult.decode_prompt}</Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current chapter event */}
      {ev && (
        <div className="litra-card p-6" data-testid="path-event">
          <div className="mono-chip green-text">{ev.chapter_name?.toUpperCase()} · CHAPTER {ev.chapter}</div>
          <h3 className="mt-2 text-xl font-display font-semibold">{ev.title}</h3>
          <p className="mt-2 text-[var(--bone-muted)] leading-relaxed" style={{ maxWidth: "68ch" }}>{ev.scenario_text}</p>
          <Separator className="my-4 bg-white/[0.06]" />
          <div className="space-y-2">
            {ev.choices?.map((cc, i) => (
              <button key={i} onClick={() => choose(i)} disabled={busy}
                className="w-full text-left litra-card litra-card-hover p-4 disabled:opacity-50" data-testid={`path-choice-${i + 1}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm">{cc.label}</span>
                  <span className="mono-chip gold-text shrink-0">+{cc.xp} XP</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {Object.entries(cc.stats || {}).map(([k, v]) => (
                    <span key={k} className="mono-chip text-[var(--bone-faint)]">{k.replace(/_/g, " ")} +{v}</span>
                  ))}
                  {rewardHints(cc.reward).map((h, j) => <GoldChip key={`r${j}`} type={h.type}>{h.label}</GoldChip>)}
                </div>
              </button>
            ))}
          </div>
          {busy && <div className="mt-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>}
        </div>
      )}
    </div>
  );
}

/* ============================= REWARDS ============================= */
function InventorySection({ title, icon: Icon, count, children, testid }) {
  return (
    <div className="litra-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-display font-semibold">
          <Icon className="w-4 h-4 text-[var(--bone-muted)]" /> {title}
        </div>
        <Badge variant="outline" className="border-white/[0.12] text-[var(--bone-faint)] text-[11px]">{count}</Badge>
      </div>
      {children}
    </div>
  );
}

const EmptyHint = ({ children }) => (
  <p className="text-xs text-[var(--bone-faint)]">{children}</p>
);

function RewardsView({ rewards, equip, openChest, chestBusy }) {
  if (!rewards) return <div className="text-[var(--bone-faint)] text-sm">Loading rewards…</div>;

  const titles = rewards.titles || [];
  const frames = rewards.frames || [];
  const themes = rewards.themes || [];
  const badges = rewards.badges || [];
  const artifacts = rewards.artifacts || [];
  const wordbodies = rewards.wordbody_cards || [];
  const letterCards = Object.entries(rewards.letter_cards || {}).sort((a, b) => a[0].localeCompare(b[0]));
  const tokens = rewards.trial_tokens || { studio: 0, premium: 0 };
  const certProg = Object.entries(rewards.certificate_progress || {});
  const chest = rewards.chest || {};

  return (
    <div className="space-y-4 fade-in">
      {/* Weekly chest */}
      <div className="litra-card p-5 flex items-center justify-between flex-wrap gap-3" data-testid="path-weekly-chest">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center gold-edge bg-[rgba(176,141,60,0.10)]"><Gift className="w-5 h-5 gold-text" /></div>
          <div>
            <div className="font-display font-semibold text-sm">Weekly Reward Chest</div>
            <div className="text-xs text-[var(--bone-faint)]">
              {chest.available ? "A chest is ready to open." : "Opened. Resets next week."}
              {chest.last_roll && <> Last: <span className="gold-text">{chest.last_roll.name}</span></>}
            </div>
          </div>
        </div>
        <Button onClick={openChest} disabled={!chest.available || chestBusy}
          className="bg-[rgba(176,141,60,0.18)] gold-text border border-[rgba(176,141,60,0.45)] hover:bg-[rgba(176,141,60,0.24)] disabled:opacity-50"
          data-testid="path-open-chest-button">
          {chestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4 mr-2" />{chest.available ? "Open chest" : "Come back next week"}</>}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Titles */}
        <InventorySection title="Profile Titles" icon={Crown} count={titles.length} testid="path-inv-titles">
          {titles.length === 0 ? <EmptyHint>Earn titles by completing chapters and journeys.</EmptyHint> : (
            <div className="space-y-1.5">
              {titles.map((t) => {
                const active = rewards.active_title === t.id;
                return (
                  <div key={t.id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 bg-white/[0.03]">
                    <span className="text-sm gold-text flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" />{t.name}</span>
                    <Button size="sm" variant={active ? "default" : "outline"} disabled={active}
                      className={active ? "bg-[var(--primary)] h-7 text-xs" : "glass h-7 text-xs"}
                      onClick={() => equip("title", t.id)} data-testid={`path-equip-title-${t.id}`}>
                      {active ? <><CheckCircle2 className="w-3 h-3 mr-1" />Equipped</> : "Equip"}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </InventorySection>

        {/* Themes */}
        <InventorySection title="Cosmetic Themes" icon={Palette} count={themes.length} testid="path-inv-themes">
          <div className="space-y-1.5">
            {themes.map((t) => {
              const active = rewards.active_theme === t.id;
              return (
                <div key={t.id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 bg-white/[0.03]">
                  <span className="text-sm flex items-center gap-1.5"><Palette className="w-3.5 h-3.5 text-[var(--bone-muted)]" />{t.name}</span>
                  <Button size="sm" variant={active ? "default" : "outline"} disabled={active}
                    className={active ? "bg-[var(--primary)] h-7 text-xs" : "glass h-7 text-xs"}
                    onClick={() => equip("theme", t.id)} data-testid={`path-equip-theme-${t.id}`}>
                    {active ? <><CheckCircle2 className="w-3 h-3 mr-1" />Active</> : "Apply"}
                  </Button>
                </div>
              );
            })}
          </div>
        </InventorySection>

        {/* Frames */}
        <InventorySection title="Share-Card Frames" icon={FrameIcon} count={frames.length} testid="path-inv-frames">
          <div className="space-y-1.5">
            {frames.map((f) => {
              const active = rewards.active_frame === f.id;
              return (
                <div key={f.id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 bg-white/[0.03]">
                  <span className="text-sm flex items-center gap-1.5"><FrameIcon className="w-3.5 h-3.5 text-[var(--bone-muted)]" />{f.name}</span>
                  <Button size="sm" variant={active ? "default" : "outline"} disabled={active}
                    className={active ? "bg-[var(--primary)] h-7 text-xs" : "glass h-7 text-xs"}
                    onClick={() => equip("frame", f.id)} data-testid={`path-equip-frame-${f.id}`}>
                    {active ? <><CheckCircle2 className="w-3 h-3 mr-1" />Active</> : "Use"}
                  </Button>
                </div>
              );
            })}
          </div>
        </InventorySection>

        {/* Trial tokens */}
        <InventorySection title="Trial Tokens" icon={Ticket} count={(tokens.studio || 0) + (tokens.premium || 0)} testid="path-inv-tokens">
          <div className="flex gap-2">
            <div className="flex-1 rounded-lg px-3 py-2 bg-white/[0.03] flex items-center justify-between">
              <span className="text-sm">Studio</span><span className="gold-text font-display font-semibold">{tokens.studio || 0}</span>
            </div>
            <div className="flex-1 rounded-lg px-3 py-2 bg-white/[0.03] flex items-center justify-between">
              <span className="text-sm">Premium</span><span className="gold-text font-display font-semibold">{tokens.premium || 0}</span>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-[var(--bone-faint)]">Spend in Studio &amp; premium tools (wiring lands with later phases).</p>
        </InventorySection>

        {/* Badges */}
        <InventorySection title="Badges" icon={Award} count={badges.length} testid="path-inv-badges">
          {badges.length === 0 ? <EmptyHint>Make standout choices to earn badges.</EmptyHint> : (
            <div className="flex flex-wrap gap-1.5">
              {badges.map((b) => <GoldChip key={b.id} type="badge" testid={`path-badge-${b.id}`}>{b.name}</GoldChip>)}
            </div>
          )}
        </InventorySection>

        {/* Artifacts */}
        <InventorySection title="Artifacts" icon={Gem} count={artifacts.length} testid="path-inv-artifacts">
          {artifacts.length === 0 ? <EmptyHint>Rare artifacts drop from pivotal chapters.</EmptyHint> : (
            <div className="flex flex-wrap gap-1.5">
              {artifacts.map((a) => <GoldChip key={a.id} type="artifact" testid={`path-artifact-${a.id}`}>{a.name}</GoldChip>)}
            </div>
          )}
        </InventorySection>

        {/* Wordbody cards */}
        <InventorySection title="Wordbody Cards" icon={Type} count={wordbodies.length} testid="path-inv-wordbodies">
          {wordbodies.length === 0 ? <EmptyHint>Name things in your journey to collect wordbodies.</EmptyHint> : (
            <div className="flex flex-wrap gap-1.5">
              {wordbodies.map((w) => <span key={w.id} className="mono-chip green-text">{w.name}</span>)}
            </div>
          )}
        </InventorySection>

        {/* Letter cards */}
        <InventorySection title="Letter Cards" icon={Layers} count={letterCards.length} testid="path-inv-letters">
          {letterCards.length === 0 ? <EmptyHint>Upgrade letters by mastering chapters.</EmptyHint> : (
            <div className="flex flex-wrap gap-2">
              {letterCards.map(([letter, info]) => (
                <div key={letter} className="w-12 rounded-lg bg-white/[0.03] border border-white/[0.08] p-2 text-center" data-testid={`path-letter-${letter}`}>
                  <div className="font-display font-semibold text-base">{letter}</div>
                  <div className="text-[10px] gold-text">Lv {info.level}</div>
                </div>
              ))}
            </div>
          )}
        </InventorySection>

        {/* Certificate progress */}
        <InventorySection title="Certificate Progress" icon={ScrollText} count={certProg.length} testid="path-inv-certs">
          {certProg.length === 0 ? <EmptyHint>Path choices build progress toward certificates.</EmptyHint> : (
            <div className="space-y-1.5">
              {certProg.map(([code, n]) => (
                <div key={code} className="flex items-center justify-between text-sm rounded-lg px-3 py-2 bg-white/[0.03]">
                  <span className="flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[var(--bone-faint)]" />{code.replace(/_/g, " ")}</span>
                  <span className="green-text font-display font-semibold">{n}</span>
                </div>
              ))}
            </div>
          )}
        </InventorySection>

        {/* Universe nodes (hook) */}
        <InventorySection title="Universe Nodes" icon={Orbit} count={(rewards.universe_nodes || []).length} testid="path-inv-universe">
          {(rewards.universe_nodes || []).length === 0 ? <EmptyHint>Some choices unlock nodes for Universe Mode (next phase).</EmptyHint> : (
            <div className="flex flex-wrap gap-1.5">
              {(rewards.universe_nodes || []).map((n) => <span key={n} className="mono-chip text-[var(--bone-faint)]">{n}</span>)}
            </div>
          )}
        </InventorySection>
      </div>
    </div>
  );
}
